import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { createEvent } from 'googleanalytics';

// components
import GbpOverviewModal from 'components/Gbp/GbpOverviewModal';
import GbpClaimModal from 'components/Gbp/GbpClaimModal';
// actions
import {
    setModalVisibility,
    getGbpSearchLocations,
    getGbpLocationVerificationStatus, 
    resetGbp,
    setGbpAlertVisibility,
    getCategories,
    changeGbpLocationData,
    setGbpLocationData,
    getGbpUserLocations,
    resetGbpLocation,
    createLocation,
    linkLocation,
    getGbpLocationVerificationMethods,
    updateGbpLocationData,
    initiateGbpVerificationProcess,
    submitGbpVerificationCode,
} from 'actions/gbp';
import { fetchInternalAssignments, resetAccountState } from 'actions/account';

// selectors
import { getCurrentUserBrand } from 'selectors/user-brands';
import { getFirstAccount } from 'selectors/account';

// constants
import { GBP_STEPS, GBP_LOCATIONS_LINK, GOOGLE_BUSINESS_LINK } from 'constants/gbp';
import { CATEGORIES, ACTIONS } from 'constants/googleAnalytics';
import { BRAND_PROPERTY, GBP_VERIFICATION_STATUS } from 'constants/gbp';
import { operatingCountries } from 'constants/user';

import { extractCldrRegion } from 'components/Gbp/gbpHelper';
import { getCountryOfUser } from 'helpers/userHelper';
import GbpLocation from 'records/GbpLocation';

import {
    CLAIM_AND_VERIFICATION_PROCESS_SUCCESS_MSG,
    LINK_LOCATION_SUCCESS_MSG,
  } from 'constants/gbp';


@connect(state => ({
    user: state.user.get('data'),
    searchLocations: state.gbp.searchLocations,
    userLocations: state.gbp.userLocations,
    brand: getCurrentUserBrand(state),
    isOpen: state.gbp.get('isModalOpen'),
    step: state.gbp.get('step'),
    location: state.gbp.get('location'),
    isGbpAlertModalOpen: state.gbp.get('isAlertModalOpen'),
    account: getFirstAccount(state),
    gbpSuccessAlert: state.gbp.get('successAlert'),
    isLinkLocationAlert: state.gbp.get('isLinkLocation'),
    isLoading: state.gbp.get('loading') || state.account.get('loading'),
    isSearchLoading: state.gbp.get('isSearchLoading'),
    locationVerificationMethods: state.gbp.get('verificationMethodsOfSelectedLocation'),
    verificationProcess: state.gbp.get('verificationProcessEntity'),
    errorMessage: state.gbp.get('errorMessage'),
    stack: state.contentstack,
}))
class GbpModal extends Component {
    static propTypes = {
        dispatch: PropTypes.func,
        user: PropTypes.object,
        isOpen: PropTypes.bool,
        step: PropTypes.number,
        isGbpAlertModalOpen: PropTypes.bool,
        account: PropTypes.object,
        location: PropTypes.object,
        errorMessage: PropTypes.string,
        stack: PropTypes.object,
    };

    constructor() {
        super();
        this.state = {
            verificationStatus: null,
            categories: [],
        }
    }

    /**
     * Change the page (step)
     * @param {Number} step step
     * @param {Boolean} clearErrors should clear errors while change step
     * @returns promise
     */
    changeStep = (step, clearErrors) => this.props.dispatch(setModalVisibility(true, step, clearErrors));

    setCategories = (googleCategories) => {
        this.setState({
            categories: googleCategories.map(({ name, displayName }) => ({
                value: name,
                label: displayName,
            }))
        })
    }

    /**
     * Decrement a step to go back
     */
    goBack = () => {
        const { step: currStep, dispatch } = this.props;
        dispatch(setModalVisibility(true, currStep > GBP_STEPS.OVERVIEW ? currStep - 1 : GBP_STEPS.OVERVIEW));
    };

    /**
     * Close modal
     */
    closeModal = () => {
        this.props.dispatch(resetGbp());
    }

    /**
     * on visit GBP profile button click
     */
    onVisitGbpProfileClick = () => {
        const { user, step } = this.props;
        const { verificationStatus } = this.state;
        const { opcoId, syscoCustomerId } = user;
        const isCanadianUser = getCountryOfUser(opcoId) === operatingCountries.canada;

        createEvent(CATEGORIES.GBP, ACTIONS.REDIRECT_TO_GBP_PAGE, `${opcoId}-${syscoCustomerId}, ${ user.id }`, user);
        if (isCanadianUser || verificationStatus === GBP_VERIFICATION_STATUS.VERIFIED || step === GBP_STEPS.SUCCESS) {
            window.open(isCanadianUser ? GOOGLE_BUSINESS_LINK : GBP_LOCATIONS_LINK, '_blank');
            this.closeModal();
        } else if (verificationStatus === GBP_VERIFICATION_STATUS.IN_PROGRESS) {
            this.changeStep(GBP_STEPS.SEARCH);
        }
    }

    /**
     * on search
     * @param {String} query query string
     * @returns promise
     */
    onSearch = query => {
        this.props.dispatch(getGbpSearchLocations(query));
    }

    setGbpAlertVisibility = isVisible => this.props.dispatch(setGbpAlertVisibility(isVisible));

    /**
     * Fetch GBP categories
     * @returns {Array} list of categories
     */
    fetchCategories = () => this.props.dispatch(getCategories());

    /**
     * Fetch location informatin from crm
     */
    fetchAccountInfo = () => {
        const { dispatch, user } = this.props;
        dispatch(fetchInternalAssignments({ opcoId: user.get('opcoId'), customerKey: user.get('syscoCustomerId'), includeInactives:  true, detailed: true}))
            .then(data => {
                const account = data.accounts[0];
                dispatch(setGbpLocationData({
                    profile: null,
                    primaryCategoryId: null,
                    title: account.name,
                    regionCode: extractCldrRegion(account.country),
                    postalCode: account.postalCode,
                    administrativeArea: account.state,
                    locality: account.city,
                    streetAddress: account.street,
                    primaryPhone: account.phone,
                    latitude: account.latitude,
                    longitude: account.longitude,
                }));
            })
    }

    /**
     * Set google location information
     */
    setGoogleLocationInfo = (location) => {
        const { dispatch } = this.props;
        const locationDetails = location.toJSON().location;
        const latlng = locationDetails.latlng || {};

        dispatch(setGbpLocationData({
            profile: locationDetails.profile,
            primaryCategoryId: locationDetails.primaryCategoryId,
            title: locationDetails.title,
            regionCode: locationDetails.storefrontAddress.regionCode,
            postalCode: locationDetails.storefrontAddress.postalCode,
            administrativeArea: locationDetails.storefrontAddress.administrativeArea,
            locality: locationDetails.storefrontAddress.locality,
            streetAddress: locationDetails.storefrontAddress.addressLines.toString(), //to support multiple address lines
            primaryPhone: locationDetails.phoneNumbers.primaryPhone,
            locationId: locationDetails.locationId,
            latitude: latlng.latitude,
            longitude: latlng.longitude,
        }));
    }

    /**
     * Change location property
     * @param {String} key location field
     * @param {String} value location field value
     * @returns action
     */
    onChangeLocationData = (value, key) => this.props.dispatch(changeGbpLocationData(key, value));

    /**
     * Reset gbp location data on back
     * @returns none
     */
    resetLocationData = () => this.props.dispatch(resetGbpLocation());

    /**
     * fetch verification status of the google location id
     * @param {String} locationId google location Id
     * @returns promise
     */
    getGbpLocationVerificationStatus = locationId => this.props.dispatch(getGbpLocationVerificationStatus(locationId));

    /**
     * fetch verification options of the google location
     * @param {String} locationId google location Id
     * @returns promise
     */
    getGbpLocationVerificationOptions = locationId => this.props.dispatch(getGbpLocationVerificationMethods(locationId));

    startGbpVerificationProcess = (locationId, verificationOption) => {
        const { dispatch } = this.props;
        const requestBody = GbpLocation.generateGbpIntiateVerificationEntity(verificationOption);

        return dispatch(initiateGbpVerificationProcess(locationId, requestBody));
    }
    
    submitGbpVerificationPin = (pin, isNewLocationCreation) => {
        const { dispatch, verificationProcess, location } = this.props;
        const requestBody = GbpLocation.generateVerificationSubmitEntity(pin, verificationProcess, location);
        
        return dispatch(submitGbpVerificationCode(isNewLocationCreation, requestBody));
    }

    /**
     * Create and verify location
     * @param {Object} location gbp location details object
     * @param {boolean} isUserCreated is the location already created by the user
     * @param {Boolean} isNewLocationCreation is the user creating a new Google location (rather than claiming one that already exists)
     * @returns promise
     */
    onGbpCreateAndVerifyLocation = (location, isUserCreated, isNewLocationCreation) => this.props.dispatch(createLocation(location, isUserCreated, isNewLocationCreation));

    /**
     * Update gbp location
     */
    onGbpUpdateLocation = () => {
        const { dispatch, location } = this.props;
        const request = GbpLocation.generateGbpUpdateEntity(location);

        return dispatch(updateGbpLocationData(request));
    }

    /**
     * link already verified location
     * @param {String} locationId google location Id
     * @param {object} gbpLocationData google location data
     * @returns promise
     */
    onLinkLocation = (locationId, gbpLocationData) => this.props.dispatch(linkLocation(locationId, gbpLocationData));


    /**
     * Check if the account is GBP verified given the current brand
     * @param {Object} currentBrand brand object
     */
    static getGbpVerificationStatus = currentBrand => {
        let status = null;
        const gbpProperty = currentBrand && currentBrand.brandProperties[BRAND_PROPERTY.GBP_VERIFICATION_STATUS];

        if (gbpProperty &&
            gbpProperty.propertyType == BRAND_PROPERTY.GBP_PROPERTY_TYPE &&
            gbpProperty.propertyKey == BRAND_PROPERTY.GBP_VERIFICATION_STATUS) {
            status = gbpProperty.propertyValue;
        }
        return status;
    }

    fetchUserLocations = () => {
        this.props.dispatch(getGbpUserLocations());
    }

    setAlertVisibility = (isVisible) => {
        this.props.dispatch(setGbpAlertVisibility(isVisible));
    }

    componentWillUnmount() {
        this.props.dispatch(resetAccountState());
    }

    componentDidMount() {
        const { brand } = this.props;
        this.setState({
            verificationStatus: GbpModal.getGbpVerificationStatus(brand),
        });
    }

    componentDidUpdate({ brand: prevBrand, step: prevStep }) {
        const { brand, step } = this.props;
        const verificationStatus = GbpModal.getGbpVerificationStatus(brand);
        if (verificationStatus === GBP_VERIFICATION_STATUS.LOGGED_OUT &&
            GbpModal.getGbpVerificationStatus(prevBrand) !== GBP_VERIFICATION_STATUS.LOGGED_OUT) {
            this.setState({
                verificationStatus,
            });
        }
    }

    render() {
        const {
            user,
            searchLocations,
            userLocations,
            brand,
            isOpen,
            step,
            isGbpAlertModalOpen,
            location,
            gbpSuccessAlert,
            isLinkLocationAlert,
            isLoading,
            isSearchLoading,
            locationVerificationMethods,
            errorMessage,
            stack: { featureCallout } ,
        } = this.props;
        const isAuthenticated = this.state.verificationStatus === GBP_VERIFICATION_STATUS.IN_PROGRESS;
        const { opcoId } = user;
        const isCanadianUser = getCountryOfUser(opcoId) === operatingCountries.canada;
        const { categories } = this.state;
        return (
            <div>
                <GbpOverviewModal
                    isOpen={isOpen && (step === GBP_STEPS.OVERVIEW || step === GBP_STEPS.SUCCESS)}
                    user={user}
                    closeModal={this.closeModal}
                    onVisitGbpProfile={this.onVisitGbpProfileClick}
                    verificationStatus={this.state.verificationStatus}
                    successAlertMessage={ isLinkLocationAlert ? LINK_LOCATION_SUCCESS_MSG : CLAIM_AND_VERIFICATION_PROCESS_SUCCESS_MSG }
                    gbpSuccessAlert={ gbpSuccessAlert }
                    isCanadianUser={isCanadianUser}
                    modalContent={featureCallout.gbp_modal}
                />
                <GbpClaimModal
                    isOpen={isOpen && isAuthenticated && step > GBP_STEPS.OVERVIEW && step < GBP_STEPS.SUCCESS}
                    searchLocations={searchLocations}
                    userLocations={userLocations}
                    brand={brand}
                    changeStep={this.changeStep}
                    closeModal={this.closeModal}
                    step={step}
                    onSearch={this.onSearch}
                    isAuthenticated={ isAuthenticated }
                    isGbpAlertModalOpen={ isGbpAlertModalOpen }
                    resetLocation={this.resetLocationData}
                    getGbpLocationVerificationStatus={this.getGbpLocationVerificationStatus}
                    getGbpLocationVerificationOptions={this.getGbpLocationVerificationOptions}
                    startGbpVerificationProcess={this.startGbpVerificationProcess}
                    onCreateAndVerifyLocation={this.onGbpCreateAndVerifyLocation}
                    onGbpUpdateLocation={this.onGbpUpdateLocation}
                    location={location}
                    fetchCategories={this.fetchCategories}
                    fetchAccountInfo={this.fetchAccountInfo}
                    onChangeLocationData={this.onChangeLocationData}
                    setGoogleLocationInfo={this.setGoogleLocationInfo}
                    setAlertVisibility={this.setAlertVisibility}
                    fetchUserLocations={this.fetchUserLocations}
                    onLinkLocation={this.onLinkLocation}
                    isLoading={isLoading}
                    isSearchLoading={isSearchLoading}
                    user={user}
                    locationVerificationMethods={locationVerificationMethods}
                    submitGbpVerificationPin={this.submitGbpVerificationPin}
                    errorMessage={errorMessage}
                    categories={categories}
                    setCategories={this.setCategories}
                />
            </div>
        );
    }
}
export default GbpModal;
